import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchListFilters,
  updateStationFilters,
} from '../../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import ToggleWithIcons from '../../../../shared-components/toggle-with-icons/ToggleWithIcons';
import dashboard from '../../../../utils/api/partner/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Station performance alert chart
 * @returns {any} The result
 */
export default function StationPerformanceChart() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { filters, filterListLoading } = useAppSelector((s) => s.approvedLocation);

  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartWidth, setChartWidth] = useState({
    type: 'percentage',
    width: 100,
  });

  const [chartToggle, setChartToggle] = useState([
    {
      label: 'user',
      key: 'userCount',
      isActive: true,
    },
    {
      label: 'revenue',
      key: 'revenue',
      isActive: false,
    },
  ]);
  const [activeIndexToggle, setActiveIndexToggle] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   * @param {any} type - The type
   */
  const fetchStationPerformanceReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
    type: string,
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await dashboard.getStationPerformanceReport({
        timePeriod: range,
        type,
      });

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      setBarGraphData(
        response?.data?.map((eachData: any) => ({
          ...eachData,
          firstHalfUser: parseFloat(eachData?.firstHalfUser?.toFixed(2)) || 0,
          secondHalfUser: parseFloat(eachData?.secondHalfUser?.toFixed(2)) || 0,
          firstHalfRevenue: parseFloat(eachData?.firstHalfRevenue?.toFixed(2)) || 0,
          secondHalfRevenue: parseFloat(eachData?.secondHalfRevenue?.toFixed(2)) || 0,
        })),
      );

      setIsLoading(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleClickToggle = (key: string) => {
    setChartToggle((previousTabs) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );

    setIsLoading(true);
    fetchStationPerformanceReport(
      chartTimePeriod[activeIndexToggle]?.value || 'last24hours',
      key || 'userCount',
    );
  };

  /**
   * Function to show custom tooltip
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   * @returns {any} The result
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];
      const dataKey1 = chartToggle[0]?.isActive ? 'firstHalfUsers' : 'firstHalfRevenue';
      const dataKey2 = chartToggle[0]?.isActive ? 'secondHalfUsers' : 'secondHalfRevenue';

      return (
        <div className="station-performance-custom-tooltip-partner">
          <p className="label">{data?.payload?.stationName}</p>
          {chartToggle[0]?.isActive ? (
            <div className="tooltip-values">
              <p style={{ color: '#2AA700' }}>
                {t('firstHalfUsers')}: {data?.payload?.[dataKey1]}
              </p>
              <p style={{ color: '#E41010' }}>
                {t('secondHalfUsers')}: {data?.payload?.[dataKey2]}
              </p>
            </div>
          ) : (
            <div className="tooltip-values">
              <p style={{ color: '#2AA700' }}>
                {t('firstHalfRevenue')}: {data?.payload?.[dataKey1]}
              </p>
              <p style={{ color: '#E41010' }}>
                {t('secondHalfRevenue')}: {data?.payload?.[dataKey2]}
              </p>
            </div>
          )}
        </div>
      );
    }

    return null;
  }

  /**
   * Function to handle click event in each bar
   * @param {any} key - The key
   */
  const handleBarChartClickEvent = (key: string) => {
    dispatch(
      updateStationFilters({
        key: 'searchBy',
        value: key,
        type: 'replace',
      }),
    );

    navigate('/ev-admin/station-management');
  };

  /**
   * Custom square bar component
   * @param {any} properties - The properties
   * @returns {any} The result
   */
  function TriangleBar(properties: any) {
    const { fill, x, y, width, height, payload } = properties;

    const blockHeight = 5;
    const blockGap = 2;
    const blockCount = Math.floor(height / (blockHeight + blockGap));

    return (
      <>
        {Array.from({ length: blockCount }).map((_, index) => (
          <rect
            x={x}
            y={y + height - (index + 1) * (blockHeight + blockGap)}
            width={width}
            height={blockHeight}
            fill={fill}
            onClick={() => handleBarChartClickEvent(payload?.stationName)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </>
    );
  }

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 120,
      });
    }
  }, [barGraphData]);

  useEffect(() => {
    const filterWithOutSearchBy = filters.filter((eachFilter: any) => eachFilter.key !== 'searchBy');
    if (filterWithOutSearchBy.length === 0 && !filterListLoading) {
      dispatch(fetchListFilters());
    }
    fetchStationPerformanceReport(
      'last24hours',
      chartToggle.find((toggle) => toggle.isActive)?.key || 'userCount',
    );
  }, []);

  return (
    <div className="station-performance-chart-wrap-partner">
      <div className="station-performance-chart-header">
        <div className="header-title">
          <h6>{t('stationPerformance')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/station-management/approved')}>
            <NavigateArrow />
          </span>
        </div>
        <ToggleWithIcons
          leftMenu={{ ...chartToggle[0], label: t(chartToggle[0].label as any) }}
          rightMenu={{ ...chartToggle[1], label: t(chartToggle[1].label as any) }}
          handleClickToggle={handleClickToggle}
        />
      </div>

      <div className="label-toggle-container right">
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => t(duration.label))}
          onToggle={(index, label) => {
            const selectedDuration = chartTimePeriod.find(
              (duration) => t(duration.label) === label,
            );
            fetchStationPerformanceReport(
              selectedDuration?.value || 'last24hours',
              chartToggle.find((toggle) => toggle.isActive)?.key || 'userCount',
            );
            setActiveIndexToggle(index);
            setIsLoading(true);
          }}
          defaultActiveIndex={activeIndexToggle}
          type="chart"
        />
      </div>
      {isLoading ? (
        <center className="station-performance-loader">
          <BatteryLoader />
        </center>
      ) : (
        <div className="station-performance-chart-container">
          <ResponsiveContainer
            width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
            height="100%">
            <BarChart data={barGraphData}>
              <XAxis
                dataKey="stationName"
                axisLine
                tickLine={false}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => (value?.length > 3 ? `${value.slice(0, 3)}...` : value)}
              />
              {/* <YAxis /> */}
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar
                dataKey={
                  chartToggle.find((toggle) => toggle.isActive)?.key === 'userCount'
                    ? 'firstHalfUsers'
                    : 'firstHalfRevenue'
                }
                fill="#2AA700"
                shape={<TriangleBar />}
                barSize={5}
              />
              <Bar
                dataKey={
                  chartToggle.find((toggle) => toggle.isActive)?.key === 'userCount'
                    ? 'secondHalfUsers'
                    : 'secondHalfRevenue'
                }
                fill="#E41010"
                shape={<TriangleBar />}
                barSize={5}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="label-toggle-container center">
        <div className="device-duration-chart-label">
          <div className="each-label">
            <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
            <p>{t('firstHalf')}</p>
          </div>
          <div className="each-label">
            <div className="each-label-color" style={{ backgroundColor: '#E41010' }} />
            <p>{t('secondHalf')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
